// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SCHEDULE_LABEL_BG, ScheduleLabelChip } from "@/components/shared/schedule-label-chip";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type CaptureWindow, formatMinutes } from "@/features/stores/schedule/schedule-utils";
import {
  DAY_MINUTES,
  SCHEDULE_LABELS,
  type ScheduleLabel,
  type ScheduleStrategy,
  type TimelineSegment,
} from "@/features/stores/schedule/types";
import { cn } from "@/lib/utils";

const STEP = 5; // minute snap
const DRAG_THRESHOLD_PX = 4; // movement below this is treated as a click, not a drag
const TICK_HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24];
// A window shorter than ~5 minutes is thinner than the 1px gap inset, so left+right
// anchoring alone collapses it to nothing. Floor the rendered width so it stays visible.
const MIN_SEGMENT_PX = 3;

interface ScheduleTimelineProps {
  strategy: ScheduleStrategy;
  segments: TimelineSegment[];
  editable?: boolean;
  size?: "lg" | "sm";
  /** Render the hour axis (00…24) below the track. Defaults to the large timeline. */
  showHours?: boolean;
  /**
   * Store open/close window. Drawn twice: as a thin rail above the track (capture windows can't
   * cover it) and as "closed" shading on the backdrop, which shows through the gaps.
   * An empty window (`end <= start`) means the store is closed all day.
   */
  openWindow?: CaptureWindow;
  /** Why these hours differ from the store's usual ones (e.g. "Christmas Day") — shown in the rail's tooltip. */
  openWindowNote?: string;
  invalidIndices?: number[];
  onChange?: (segments: TimelineSegment[]) => void;
}

function snap(min: number): number {
  return Math.max(0, Math.min(DAY_MINUTES, Math.round(min / STEP) * STEP));
}

/** Swallow the click synthesized right after a drag so it doesn't open the popover. */
function suppressNextClick() {
  function swallow(ev: MouseEvent) {
    ev.stopPropagation();
    ev.preventDefault();
  }
  document.addEventListener("click", swallow, { capture: true, once: true });
  setTimeout(() => document.removeEventListener("click", swallow, true), 0);
}

export function ScheduleTimeline({
  strategy,
  segments,
  editable = false,
  size = "lg",
  showHours = size === "lg",
  openWindow,
  openWindowNote,
  invalidIndices,
  onChange,
}: ScheduleTimelineProps) {
  const { t } = useTranslation("back-office");
  const trackRef = useRef<HTMLDivElement>(null);
  // Segment whose edge/pin is being dragged — its tooltip is force-shown during the drag.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const isStrict = strategy === "strict";
  // Edge-drag/move applies to clock-anchored windows; derived strategies only edit labels.
  const canEditTimes = editable && strategy === "static";
  const canAddPins = editable && isStrict;
  const invalid = new Set(invalidIndices ?? []);
  const closedAllDay = !!openWindow && openWindow.end <= openWindow.start;

  function minutesFromClientX(clientX: number): number {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return snap(ratio * DAY_MINUTES);
  }

  function setLabel(index: number, label: ScheduleLabel) {
    onChange?.(segments.map((s, i) => (i === index ? { ...s, label } : s)));
  }

  function removeSegment(index: number) {
    onChange?.(segments.filter((_, i) => i !== index));
  }

  function setRange(index: number, start: number, end: number) {
    onChange?.(segments.map((s, i) => (i === index ? { ...s, start, end } : s)));
  }

  function startEdgeDrag(index: number, edge: "start" | "end", e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragIndex(index);
    function onMove(ev: PointerEvent) {
      const m = minutesFromClientX(ev.clientX);
      const seg = segments[index];
      if (!seg) return;
      if (edge === "start") setRange(index, Math.min(m, seg.end - STEP), seg.end);
      else setRange(index, seg.start, Math.max(m, seg.start + STEP));
    }
    function onUp() {
      setDragIndex(null);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      // Edge handles never open the popover — always swallow the trailing click.
      suppressNextClick();
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  function startPinDrag(index: number, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startClientX = e.clientX;
    let moved = false;
    function onMove(ev: PointerEvent) {
      if (!moved && Math.abs(ev.clientX - startClientX) < DRAG_THRESHOLD_PX) return;
      moved = true;
      setDragIndex(index);
      const m = minutesFromClientX(ev.clientX);
      onChange?.(segments.map((s, i) => (i === index ? { ...s, start: m, end: m } : s)));
    }
    function onUp() {
      setDragIndex(null);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      // A tap (no move) should still open the pin's popover; only suppress after a real drag.
      if (moved) suppressNextClick();
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  // Drag a whole window left/right, keeping its duration, within the gap between neighbours.
  function startMoveDrag(index: number, e: React.PointerEvent) {
    const seg = segments[index];
    if (!seg) return;
    const origStart = seg.start;
    const dur = seg.end - seg.start;
    const others = segments.filter((_, i) => i !== index);
    const leftLimit = others.filter((o) => o.end <= seg.start).reduce((m, o) => Math.max(m, o.end), 0);
    const rightLimit = others.filter((o) => o.start >= seg.end).reduce((m, o) => Math.min(m, o.start), DAY_MINUTES);
    const startClientX = e.clientX;
    const startMin = minutesFromClientX(e.clientX);
    let moved = false;
    function onMove(ev: PointerEvent) {
      if (!moved && Math.abs(ev.clientX - startClientX) < DRAG_THRESHOLD_PX) return;
      moved = true;
      setDragIndex(index);
      const delta = minutesFromClientX(ev.clientX) - startMin;
      const newStart = Math.max(leftLimit, Math.min(rightLimit - dur, snap(origStart + delta)));
      setRange(index, newStart, newStart + dur);
    }
    function onUp() {
      setDragIndex(null);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      // A tap (no move) opens the label popover; only suppress the click after an actual move.
      if (moved) suppressNextClick();
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  function addPinAt(clientX: number) {
    if (!canAddPins) return;
    const m = minutesFromClientX(clientX);
    const next = [...segments, { start: m, end: m, label: "model" as ScheduleLabel }].sort((a, b) => a.start - b.start);
    onChange?.(next);
  }

  const trackHeight = size === "lg" ? "h-14" : "h-7";

  return (
    <div className="space-y-1">
      {openWindow && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn("relative w-full overflow-hidden rounded-full bg-muted", size === "lg" ? "h-1.5" : "h-1")}
            >
              <div
                className="absolute inset-y-0 rounded-full bg-foreground/30"
                style={{
                  left: `${(openWindow.start / DAY_MINUTES) * 100}%`,
                  right: `${100 - (openWindow.end / DAY_MINUTES) * 100}%`,
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top">
            {closedAllDay
              ? t("stores.schedule.closedAllDay")
              : t("stores.schedule.openHours", {
                  range: `${formatMinutes(openWindow.start)}–${formatMinutes(openWindow.end)}`,
                })}
            {openWindowNote ? ` · ${openWindowNote}` : ""}
          </TooltipContent>
        </Tooltip>
      )}
      <div
        ref={trackRef}
        className={cn(
          "relative w-full overflow-hidden rounded-md border bg-[repeating-linear-gradient(45deg,transparent,transparent_6px,var(--color-muted)_6px,var(--color-muted)_12px)]",
          trackHeight,
          canAddPins && "cursor-copy",
        )}
        onPointerDown={canAddPins ? (e) => addPinAt(e.clientX) : undefined}
      >
        {openWindow && openWindow.start > 0 && (
          <div
            className="pointer-events-none absolute inset-y-0 left-0 bg-black/10 dark:bg-black/25"
            style={{ width: `${(openWindow.start / DAY_MINUTES) * 100}%` }}
          />
        )}
        {openWindow && openWindow.end < DAY_MINUTES && (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 bg-black/10 dark:bg-black/25"
            style={{ width: `${((DAY_MINUTES - openWindow.end) / DAY_MINUTES) * 100}%` }}
          />
        )}
        {!isStrict &&
          segments.map((seg, i) => {
            const left = (seg.start / DAY_MINUTES) * 100;
            const right = (seg.end / DAY_MINUTES) * 100;
            const width = right - left;
            const rangeLabel = `${formatMinutes(seg.start)}–${formatMinutes(seg.end)}`;
            // Anchor the right edge to the end boundary (via `right`) and inset the left by 1px.
            // Two adjacent windows then reference the same boundary %, so they round to the same
            // device pixel and the gap is always exactly 1px — independent left%+width% rounding
            // would otherwise drift to 2px on some boundaries.
            const boxStyle = {
              // The `min()` keeps a window that ends at 24:00 from starting past the track's right
              // edge, where `overflow-hidden` would clip the min-width sliver away again.
              left: `min(calc(${left}% + 1px), calc(100% - ${MIN_SEGMENT_PX}px))`,
              right: `calc(${100 - right}%)`,
              // Over-constrained (left + right + min-width): the browser drops `right`, so a
              // sub-pixel window grows rightwards to MIN_SEGMENT_PX instead of vanishing.
              minWidth: `${MIN_SEGMENT_PX}px`,
            };
            const title = `${t(`stores.schedule.labels.${seg.label}`)} · ${rangeLabel}`;
            const visibleLabel = size === "lg" && width > 9 ? t(`stores.schedule.labels.${seg.label}`) : null;
            const boxClass = cn(
              "absolute inset-y-0 flex items-center justify-center overflow-hidden rounded-[2px] text-[10px] font-medium text-white/95",
              SCHEDULE_LABEL_BG[seg.label],
              invalid.has(i) && "ring-2 ring-destructive ring-inset",
            );

            // Read-only (e.g. exception preview): plain swatch with a tooltip.
            if (!editable) {
              return (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <div style={boxStyle} className={cn(boxClass, "cursor-default")}>
                      {visibleLabel}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{title}</TooltipContent>
                </Tooltip>
              );
            }
            // Editable: every window opens a label popover. Edge-resize + remove only for
            // clock-anchored windows (static); derived windows edit labels only.
            return (
              <Popover key={i}>
                <Tooltip open={dragIndex === i ? true : undefined}>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onPointerDown={canEditTimes ? (e) => startMoveDrag(i, e) : undefined}
                        style={boxStyle}
                        className={cn(
                          boxClass,
                          canEditTimes ? "cursor-grab" : "cursor-pointer",
                          "transition-[filter] hover:brightness-110",
                        )}
                      >
                        {visibleLabel}
                        {canEditTimes && (
                          <>
                            <span
                              onPointerDown={(e) => startEdgeDrag(i, "start", e)}
                              className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize bg-white/40"
                            />
                            <span
                              onPointerDown={(e) => startEdgeDrag(i, "end", e)}
                              className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize bg-black/20"
                            />
                          </>
                        )}
                      </button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{title}</TooltipContent>
                </Tooltip>
                <SegmentPopover
                  seg={seg}
                  showTimes={canEditTimes}
                  onLabel={(l) => setLabel(i, l)}
                  onRange={(s, e) => setRange(i, s, e)}
                  onRemove={canEditTimes ? () => removeSegment(i) : undefined}
                />
              </Popover>
            );
          })}

        {isStrict &&
          segments.map((seg, i) => {
            const left = (seg.start / DAY_MINUTES) * 100;
            const pinTitle = `${t(`stores.schedule.labels.${seg.label}`)} · ${formatMinutes(seg.start)}`;
            if (!editable) {
              return (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <div
                      style={{ left: `${left}%` }}
                      className={cn(
                        "absolute inset-y-1 w-1 -translate-x-1/2 rounded-full",
                        SCHEDULE_LABEL_BG[seg.label],
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{pinTitle}</TooltipContent>
                </Tooltip>
              );
            }
            return (
              <Popover key={i}>
                <Tooltip open={dragIndex === i ? true : undefined}>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onPointerDown={(e) => startPinDrag(i, e)}
                        style={{ left: `${left}%` }}
                        className={cn(
                          "absolute inset-y-1 w-1 -translate-x-1/2 cursor-ew-resize rounded-full",
                          SCHEDULE_LABEL_BG[seg.label],
                        )}
                      />
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{pinTitle}</TooltipContent>
                </Tooltip>
                <SegmentPopover
                  seg={seg}
                  pin
                  onLabel={(l) => setLabel(i, l)}
                  onRange={(s) => setRange(i, s, s)}
                  onRemove={() => removeSegment(i)}
                />
              </Popover>
            );
          })}
      </div>

      {showHours && (
        <div className="relative h-4 text-[10px] text-muted-foreground">
          {TICK_HOURS.map((h) => (
            <span
              key={h}
              style={{ left: `${(h / 24) * 100}%` }}
              className={cn(
                "absolute -translate-x-1/2 font-mono tabular-nums",
                h === 0 && "translate-x-0",
                h === 24 && "-translate-x-full",
              )}
            >
              {String(h).padStart(2, "0")}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface SegmentPopoverProps {
  seg: TimelineSegment;
  pin?: boolean;
  showTimes?: boolean;
  onLabel: (label: ScheduleLabel) => void;
  onRange: (start: number, end: number) => void;
  onRemove?: () => void;
}

function SegmentPopover({ seg, pin, showTimes, onLabel, onRange, onRemove }: SegmentPopoverProps) {
  const { t } = useTranslation("back-office");
  return (
    <PopoverContent align="center" className="w-56 space-y-3">
      <div className="text-xs font-medium text-muted-foreground">
        {pin
          ? t("stores.schedule.popover.slotAt", { time: formatMinutes(seg.start) })
          : t("stores.schedule.popover.window", { range: `${formatMinutes(seg.start)}–${formatMinutes(seg.end)}` })}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {SCHEDULE_LABELS.map((label) => (
          <ScheduleLabelChip key={label} label={label} selected={seg.label === label} onClick={() => onLabel(label)} />
        ))}
      </div>
      {(showTimes || pin) && (
        <div className="flex items-center gap-2">
          <TimeInput value={seg.start} onChange={(v) => onRange(v, pin ? v : seg.end)} />
          {!pin && (
            <>
              <span className="text-muted-foreground">–</span>
              <TimeInput value={seg.end} onChange={(v) => onRange(seg.start, v)} />
            </>
          )}
        </div>
      )}
      {onRemove && (
        <Button type="button" variant="ghost" size="sm" className="w-full text-destructive" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
          {t("stores.schedule.popover.remove")}
        </Button>
      )}
    </PopoverContent>
  );
}

function TimeInput({ value, onChange }: { value: number; onChange: (minutes: number) => void }) {
  return (
    <input
      type="time"
      value={formatMinutes(value)}
      step={STEP * 60}
      onChange={(e) => {
        const [hs, ms] = e.target.value.split(":");
        const h = Number(hs);
        const m = Number(ms);
        if (Number.isFinite(h) && Number.isFinite(m)) onChange(h * 60 + m);
      }}
      className="flex-1 rounded-md border bg-transparent px-2 py-1 text-sm tabular-nums"
    />
  );
}
